import i18next, { type i18n as I18nInstance } from 'i18next';
import { SUPPORTED_LANGUAGES } from '@/config/langPath';

export interface TranslationResponse {
  translation: string;
  requestedLng: string;
  usedLng: string;
}

export interface FunnelTranslations {
  i18n: I18nInstance;
  init(): Promise<void>;
  translate(keys: string[], context: Record<string, unknown>): TranslationResponse | undefined;
  addListener(type: 'translationLoaded', listener: () => void): void;
  removeListener(type: 'translationLoaded', listener: () => void): void;
  /** Extra interpolation context for every translate (`userData`, paywall currency); merged under the DivKit variables. */
  setContext(extra: Record<string, unknown>): void;
  /** Money in the paywall currency, for strings that take a ready value (`{{price}}`) instead of a path. */
  formatPrice(amount: number): string;
}

/**
 * Paywall strings from WebPaywalls use i18next's default `{{path, formatter}}` syntax
 * (`{{priceListItem.price.price, priceFormat}}`, `{{userData.name}}`), while quiz strings use `@{var}`.
 * The instance is configured for `@{}`; strings that still carry `{{` get a second pass with the legacy syntax.
 */
const LEGACY_INTERPOLATION = { prefix: '{{', suffix: '}}', escapeValue: false, formatSeparator: ',' } as const;

const priceFormat = (value: unknown, lng: string | undefined, options: { currency?: string } = {}): string => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return String(value ?? '');
  try {
    return new Intl.NumberFormat(lng || 'en', { style: 'currency', currency: options.currency || 'USD' }).format(
      amount,
    );
  } catch {
    return amount.toFixed(2);
  }
};

const instances = new Map<string, FunnelTranslations>();

/**
 * One instance per language for the whole app run. Every route used to build its own i18next and fetch the
 * locales again; now the quiz, the paywall and the post-payment screens share the instance and the
 * `init()` promise, so the locale files go over the wire once.
 */
export const createTranslations = (language = 'en'): FunnelTranslations => {
  const key = language.toLowerCase();
  let translations = instances.get(key);
  if (!translations) {
    translations = buildTranslations(language);
    instances.set(key, translations);
  }
  return translations;
};

/** Tests only: drops the shared instances so a test starts from a cold i18next. */
export const resetTranslations = (): void => {
  instances.clear();
};

/** The key has a non-empty text in the current language itself (no fallback); '' = exported untranslated. */
const hasOwnText = (instance: I18nInstance, key: string): boolean => {
  const value = instance.getResource(instance.resolvedLanguage ?? instance.language, 'translation', key) as unknown;
  return typeof value === 'string' && value !== '';
};

const buildTranslations = (language: string): FunnelTranslations => {
  const instance = i18next.createInstance();
  const listeners = new Set<() => void>();
  let extraContext: Record<string, unknown> = {};
  let ready: Promise<void> | undefined;

  const init = async () => {
    await instance.init({
      lng: language,
      fallbackLng: 'en',
      supportedLngs: [...SUPPORTED_LANGUAGES],
      keySeparator: '.',
      interpolation: { escapeValue: false, prefix: '@{', suffix: '}' },
      resources: {
        en: { translation: { funnel: { greeting: 'Welcome to your plan', fallbackOnly: 'English fallback' } } },
        ru: { translation: { funnel: { greeting: 'Добро пожаловать в ваш план' } } },
      },
    });
    instance.services.formatter?.add('priceFormat', (value, lng, options) =>
      priceFormat(value, lng, { currency: (extraContext.currency as string | undefined) ?? options?.currency }),
    );
    await loadLocale(instance, language, listeners);
    if (language !== 'en') await loadLocale(instance, 'en', listeners);
    listeners.forEach((listener) => listener());
  };

  return {
    i18n: instance,
    init() {
      // Concurrent and repeated callers share one init; a failed one is retried on the next call.
      ready ??= init().catch((error: unknown) => {
        ready = undefined;
        throw error;
      });
      return ready;
    },
    translate(keys, context) {
      // A key in the current language beats an earlier key that only exists in the fallback: a hook text
      // present in `en` alone must not replace the `ru` default (`exists()` always consults the fallback).
      const key =
        keys.find((candidate) => hasOwnText(instance, candidate)) ??
        keys.find((candidate) => instance.exists(candidate));
      if (key !== undefined) {
        const merged = { ...extraContext, ...context };
        const result = instance.t(key, { ...merged, returnDetails: true }) as {
          res: string;
          usedLng: string;
        };
        const translation = result.res.includes('{{')
          ? (instance.t(key, { ...merged, interpolation: LEGACY_INTERPOLATION }) as string)
          : result.res;
        return {
          translation,
          requestedLng: instance.language,
          usedLng: result.usedLng,
        };
      }

      return undefined;
    },
    addListener(type, listener) {
      if (type === 'translationLoaded') listeners.add(listener);
    },
    removeListener(type, listener) {
      if (type === 'translationLoaded') listeners.delete(listener);
    },
    setContext(extra) {
      extraContext = { ...extraContext, ...extra };
      listeners.forEach((listener) => listener());
    },
    formatPrice(amount) {
      return priceFormat(amount, instance.language || language, {
        currency: extraContext.currency as string | undefined,
      });
    },
  };
};

/**
 * Hook texts (PRD-4996) live next to the Tolgee snapshot as `hooks.json` — a hand-kept dictionary
 * (`<key>__<hook>`) until the keys move to Tolgee. Loaded after `translation.json` without overwriting it,
 * so a Tolgee key always wins; a missing file is silent.
 */
const HOOKS_FILE = 'hooks.json';

const loadLocale = async (instance: I18nInstance, language: string, listeners: Set<() => void>) => {
  try {
    const response = await fetch(`/locales/${language}/translation.json`);
    if (!response.ok) throw new Error(`Locale ${language} returned HTTP ${response.status}`);
    instance.addResourceBundle(language, 'translation', await response.json(), true, true);
    listeners.forEach((listener) => listener());
  } catch {
    // The committed resources keep local tests and offline development deterministic.
  }
  try {
    const response = await fetch(`/locales/${language}/${HOOKS_FILE}`);
    if (!response.ok) return;
    instance.addResourceBundle(language, 'translation', await response.json(), true, false);
    listeners.forEach((listener) => listener());
  } catch {
    // Optional file: nothing to do without it.
  }
};
